// src/components/FD/ApplicationRegistration.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { getTodayYYYYMMDD } from '../../utils/dateUtils';
import './ApplicationRegistration.css';

const ApplicationRegistration = () => {
  const [formData, setFormData] = useState({
    name: '',
    registration_no: '',
    phone_no: '',
    date: getTodayYYYYMMDD()
  });

  const [errors, setErrors] = useState({});
  const [registrationNo, setRegistrationNo] = useState(1);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    const fetchLatestRegistration = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/rd/latest-registration`);
        const latestRegNo = response.data.latestRegNo || 0;
        setRegistrationNo(latestRegNo + 1);
        setFormData(prev => ({
          ...prev,
          registration_no: `RD${String(latestRegNo + 1).padStart(3, '0')}`
        }));
      } catch {
        setRegistrationNo(1);
        setFormData(prev => ({ ...prev, registration_no: 'RD001' }));
      }
    };

    const fetchPartners = async () => {
      const loadPartnerList = async (endpoint) => {
        try {
          const response = await axios.get(`${API_BASE_URL}${endpoint}`);
          return Array.isArray(response.data) ? response.data : [];
        } catch {
          return [];
        }
      };

      const [generalPartners, cdPartners] = await Promise.all([
        loadPartnerList('/partners'),
        loadPartnerList('/cd-partners')
      ]);

      const uniquePartners = [...new Set([...generalPartners, ...cdPartners].filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
      setPartners(uniquePartners);
    };

    fetchLatestRegistration();
    fetchPartners();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => {
        const newErr = { ...prev };
        delete newErr[name];
        return newErr;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.date) newErrors.date = 'Date is required';
    
    // Validate phone number format (basic validation)
    if (formData.phone_no && !/^\d{10}$/.test(formData.phone_no.replace(/\D/g, ''))) {
      newErrors.phone_no = 'Please enter a valid 10-digit phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      alert('Please fill all required fields correctly.');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/rd/new-application`, formData);
      if (response.data.success) {
        alert('Application created successfully!');
        setFormData({
          name: '',
          registration_no: `RD${String(registrationNo + 1).padStart(3, '0')}`,
          phone_no: '',
          date: getTodayYYYYMMDD()
        });
        setRegistrationNo(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error creating application:', error);
      alert(error.response?.data?.error || 'Failed to create application.');
    }
  };

  return (
    <div className="fd-container">
      <form className="fd-form" onSubmit={handleSubmit}>
        <h1 className="fd-title">RD Application Registration</h1>

        <div className="fd-section">
          <div>
            <label>Registration No:</label>
            <input type="text" value={formData.registration_no} readOnly />
          </div>
          <div>
            <label>Date: <span className="fd-required">*</span></label>
            <input type="date" name="date" value={formData.date} onChange={handleChange} required />
            {errors.date && <p className="fd-error">{errors.date}</p>}
          </div>
        </div>

        <h3 className="fd-subtitle">Application Details</h3>
        <div className="fd-section">
          <div>
            <label>Partner Name: <span className="fd-required">*</span></label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              list="rd-partner-options"
              placeholder="Select or enter partner"
            />
            <datalist id="rd-partner-options">
              {partners.map(partner => (
                <option key={partner} value={partner} />
              ))}
            </datalist>
            {errors.name && <p className="fd-error">{errors.name}</p>}
          </div>
          <div>
            <label>Phone No:</label>
            <input type="text" name="phone_no" value={formData.phone_no} onChange={handleChange} maxLength="10" />
            {errors.phone_no && <p className="fd-error">{errors.phone_no}</p>}
          </div>
        </div>

        <div className="fd-submit-container">
          <button type="submit" className="fd-submit">Submit Application</button>
        </div>
      </form>
    </div>
  );
};

export default ApplicationRegistration;

